import React from 'react';
import { FontAwesome6 } from '@expo/vector-icons';
import { Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { FeedPaper } from '../../lib/hooks/useProjectFeed';
import {
    buildListMetadata,
    formatAuthors,
    getFeedbackStatusIcon,
    getFeedbackStatusLabel,
} from './projectFeedUi';
import { useLingui } from '@lingui/react';
import { resolveMessage } from '../../lib/i18n/messages';

interface ProjectFeedListItemProps {
    item: FeedPaper;
    selected: boolean;
    onPress: () => void;
    testID?: string;
}

export default function ProjectFeedListItem({
    item,
    selected,
    onPress,
    testID,
}: ProjectFeedListItemProps) {
    const { fontsLoaded } = useFontLoading();
    const { i18n } = useLingui();
    const metadata = buildListMetadata(item).map((value) => resolveMessage(i18n, value)).join(' • ');
    const feedbackMessage = getFeedbackStatusLabel(item.project_paper_feedback);
    const feedbackLabel = feedbackMessage ? i18n._(feedbackMessage) : null;
    const feedbackIcon = getFeedbackStatusIcon(item.project_paper_feedback);

    return (
        <TouchableOpacity
            style={[styles.card, selected && styles.cardSelected]}
            onPress={onPress}
            activeOpacity={0.82}
            testID={testID}
            accessibilityRole="button"
            accessibilityState={{ selected }}
        >
            <View style={styles.headerRow}>
                <Text
                    style={[styles.title, fontsLoaded && interSemiBold]}
                    numberOfLines={2}
                >
                    {item.paper.title}
                </Text>
                {feedbackIcon && feedbackLabel ? (
                    <View
                        style={styles.badge}
                        testID={`${testID ? `${testID}-status` : 'project-feed-row-status'}`}
                        accessibilityLabel={feedbackLabel}
                    >
                        <FontAwesome6
                            name={feedbackIcon}
                            size={12}
                            color={feedbackIcon === 'star' ? colors.starYellow : colors.text700}
                            solid={feedbackIcon === 'star'}
                        />
                    </View>
                ) : null}
            </View>

            <Text
                style={[styles.authors, fontsLoaded && interRegular]}
                numberOfLines={1}
            >
                {formatAuthors(item.paper.authors)}
            </Text>

            {metadata ? (
                <Text
                    style={[styles.metadata, fontsLoaded && interRegular]}
                    numberOfLines={1}
                >
                    {metadata}
                </Text>
            ) : null}
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    card: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 14,
        backgroundColor: colors.card,
        paddingHorizontal: 16,
        paddingVertical: 14,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    cardSelected: {
        borderColor: '#cdd9c6',
        borderWidth: 2,
        backgroundColor: '#fbfcf9',
    },
    headerRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        marginBottom: 8,
    },
    title: {
        flex: 1,
        fontSize: 16,
        lineHeight: 22,
        color: colors.text900,
    },
    authors: {
        fontSize: 13,
        lineHeight: 18,
        color: colors.text600,
        marginBottom: 6,
    },
    metadata: {
        fontSize: 12,
        lineHeight: 17,
        color: colors.text500,
    },
    badge: {
        width: 30,
        height: 30,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: '#d7dbe3',
        backgroundColor: '#f7f8fb',
        alignItems: 'center',
        justifyContent: 'center',
    },
});
